import { getUpstreamComponentIds } from './placeholderExtractor';

const TYPES_REQUIRING_SECRETS = new Set([
  'api_request_and_response',
  'api_export',
  'code',
  'advanced_code',
  'trigger_from_api_polling',
  'await_external_event',
  'http_request',
]);

const normalizePlaceholderArray = rawPlaceholders => {
  if (!Array.isArray(rawPlaceholders)) return [];

  const seen = new Set();
  const normalized = [];

  for (const item of rawPlaceholders) {
    const key = item?.key != null ? String(item.key) : '';
    if (!key) continue;

    const type = item?.type ? String(item.type) : 'text';
    const tuple = `${key}:${type}`;
    if (seen.has(tuple)) continue;

    seen.add(tuple);
    normalized.push({ key, type });
  }

  return normalized;
};

export function normalizeLegacyPlaceholderMap(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  const map = {};
  for (const [componentId, value] of Object.entries(raw)) {
    if (Array.isArray(value)) {
      map[componentId] = normalizePlaceholderArray(value);
      continue;
    }

    if (Array.isArray(value?.placeholders)) {
      map[componentId] = normalizePlaceholderArray(value.placeholders);
      continue;
    }

    map[componentId] = [];
  }

  return map;
}

export function normalizeV2PlaceholderMap(rawV2) {
  const components = rawV2?.components;
  if (!components || typeof components !== 'object') return {};

  const map = {};
  for (const [componentId, componentValue] of Object.entries(components)) {
    map[componentId] = normalizePlaceholderArray(componentValue?.exports);
  }

  return map;
}

const toTupleSet = placeholderMap => {
  const tuples = new Set();

  for (const [componentId, placeholders] of Object.entries(placeholderMap || {})) {
    const normalized = normalizePlaceholderArray(placeholders);
    for (const item of normalized) {
      tuples.add(`${componentId}:${item.key}:${item.type}`);
    }
  }

  return tuples;
};

export function comparePlaceholderMaps(canonicalMap, fallbackMap) {
  const canonicalSet = toTupleSet(canonicalMap);
  const fallbackSet = toTupleSet(fallbackMap);

  const onlyCanonical = [...canonicalSet].filter(tuple => !fallbackSet.has(tuple)).sort();
  const onlyFallback = [...fallbackSet].filter(tuple => !canonicalSet.has(tuple)).sort();

  return {
    isMatch: onlyCanonical.length === 0 && onlyFallback.length === 0,
    canonicalCount: canonicalSet.size,
    fallbackCount: fallbackSet.size,
    onlyCanonical,
    onlyFallback,
  };
}

export function emitPlaceholderParityTelemetry({ projectId, workflowVersion, comparison }) {
  if (!comparison || comparison.isMatch) return;
}

export function computePlaceholdersForComponentFromOutputMap(
  targetId,
  components,
  placeholdersByComponent,
  globals = [],
  credentials = []
) {
  if (!targetId || !components) return [{ placeholders: [] }];

  const upstreamIds = getUpstreamComponentIds(targetId, components);
  const compMap = {};
  for (const comp of components) {
    compMap[comp.componentId] = comp;
  }

  const placeholders = [];

  for (const upstreamId of upstreamIds) {
    placeholders.push(...normalizePlaceholderArray(placeholdersByComponent?.[upstreamId] || []));
  }

  const target = compMap[targetId];
  if (target && TYPES_REQUIRING_SECRETS.has(target.type)) {
    placeholders.push(...normalizePlaceholderArray(credentials));
  }

  placeholders.push(...normalizePlaceholderArray(globals));

  return [{ placeholders: normalizePlaceholderArray(placeholders) }];
}

export function mergePlaceholderResponses(primaryResponse, fallbackResponse) {
  const primary = normalizePlaceholderArray(primaryResponse?.[0]?.placeholders || []);
  const fallback = normalizePlaceholderArray(fallbackResponse?.[0]?.placeholders || []);

  return [{ placeholders: normalizePlaceholderArray([...primary, ...fallback]) }];
}
